import { useMemo } from 'react'

import { ViewColumnTemplateFragment } from 'types/graphql'

export type ColumnSection = {
  key: string
  label: string
  isCollapsible: boolean
  templates: ViewColumnTemplateFragment[]
}

export type FilteredSection = ColumnSection & {
  filteredTemplates: ViewColumnTemplateFragment[]
  isExpanded: boolean
  isEmpty: boolean
}

export type FilteredDrawerState = {
  sections: FilteredSection[]
  hasAnyMatch: boolean
  isSearching: boolean
}

export const matchesSearch = (label: string, query: string): boolean => {
  if (!query) {
    return true
  }
  return label.toLowerCase().includes(query.toLowerCase())
}

export const filterColumnTemplates = (
  templates: ViewColumnTemplateFragment[],
  searchQuery: string
): ViewColumnTemplateFragment[] => {
  if (!searchQuery) {
    return templates
  }
  return templates.filter((t) => matchesSearch(t.labelSentenceCase, searchQuery))
}

export const getFilteredDrawerState = (args: {
  sections: ColumnSection[]
  searchQuery: string
  collapsedSectionKeys: Set<string>
}): FilteredDrawerState => {
  const { sections, searchQuery, collapsedSectionKeys } = args
  const isSearching = searchQuery.length > 0

  const filtered: FilteredSection[] = sections.map((section) => {
    const filteredTemplates = filterColumnTemplates(section.templates, searchQuery)
    const isCollapsed = collapsedSectionKeys.has(section.key)

    return {
      ...section,
      filteredTemplates,
      isExpanded: isSearching || !section.isCollapsible ? true : !isCollapsed,
      isEmpty: filteredTemplates.length === 0,
    }
  })

  const hasAnyMatch = filtered.some((s) => s.filteredTemplates.length > 0)

  return { sections: filtered, hasAnyMatch, isSearching }
}

export const shouldShowSection = (section: FilteredSection, isSearching: boolean): boolean => {
  if (!isSearching) {
    return true
  }
  if (!section.isCollapsible) {
    return true
  }
  return section.filteredTemplates.length > 0
}

// Hook wrapper around `getFilteredDrawerState` so callers don't have to wire up their own
// `useMemo`. The pure function is still exported for unit testing.
export const useFilteredDrawerState = (args: {
  sections: ColumnSection[]
  searchQuery: string
  collapsedSectionKeys: Set<string>
}): FilteredDrawerState => {
  const { sections, searchQuery, collapsedSectionKeys } = args
  return useMemo(
    () => getFilteredDrawerState({ sections, searchQuery, collapsedSectionKeys }),
    [sections, searchQuery, collapsedSectionKeys]
  )
}
